/*
Copyright (c) Uber Technologies, Inc.

This source code is licensed under the MIT license found in the
LICENSE file in the root directory of this source tree.
*/

// Accessibility helpers for the Menu's opt-in `rovingFocus` mode.
//
// --- KNOWN LIMITATION: iOS VoiceOver swipe leaks past the last option -------
//
// TODO(a11y): The open listbox does NOT trap the iOS VoiceOver swipe cursor. On
// iOS, swiping right past the LAST option (or left past the first) walks the VO
// cursor straight out of the menu into the rest of the page instead of staying
// within the list. This is a pre-existing/latent bug shared by every Menu-backed
// dropdown (regular Select, TimePicker, PhoneInput country picker) and is also
// reproducible on `main` — `rovingFocus` does not address it.
//
// Why our existing traps don't help:
//   - VoiceOver swipe navigation walks the ACCESSIBILITY TREE in reading order.
//     It is not focus-based: it does not move DOM focus and does not fire
//     keydown. So the JS keyboard traps (arrow-key handlers, roving tabindex,
//     react-focus-lock) that constrain Tab/arrow navigation have no effect on
//     the swipe cursor.
//
// Why `aria-modal` is NOT a fix here:
//   - `aria-modal="true"` is only meaningfully honored on a `role="dialog"` /
//     `role="alertdialog"` container. These popups are `role="listbox"`, so it
//     is semantically wrong and effectively ignored by AT.
//   - Even on real dialogs, iOS Safari/VoiceOver honoring of `aria-modal` to
//     constrain the swipe cursor is inconsistent across OS versions.
//
// The only robust fix:
//   - While the menu is open, remove everything else from the accessibility
//     tree — mark all sibling/background content `inert` (or `aria-hidden`) and
//     restore it on close (the "aria-modal polyfill" / `ariaHideOutside` /
//     `hideOthers` pattern). With no background in the AX tree, the swipe cursor
//     has nowhere to leak.
//   - This is deferred because it is non-trivial: these menus render in a
//     Layer/portal at <body> level, so it must hide every other top-level node
//     (excluding the open layer), revert precisely on close across the portal
//     boundary, and cope with nested menus / multiple concurrent layers. It also
//     makes the dropdown behave modally (trigger, field label, and the rest of
//     the page become invisible to AT while open), so it should ship as an
//     explicit opt-in (mirroring `rovingFocus`) rather than a global change.
//
// --- Per-document pointer-move tracking ------------------------------------
//
// Suppress hover-highlight that wasn't caused by a genuine pointer move.
//
// The menu highlights options on `mouseenter`. But a `mouseenter` also fires
// when the LIST SCROLLS under a stationary cursor — on open (centering the
// selected row), when the browser scrolls a freshly focused option into view,
// and while VoiceOver navigates. Each such scroll would otherwise produce a
// spurious highlight change that scrolls again: an infinite ping-pong that
// manifests as the scroll position locking/snapping (notably on macOS
// VoiceOver).
//
// Fix: only honor a `mouseenter` if the user actually moved the pointer since we
// last consumed one. A genuine hover is always preceded by a `mousemove`; a
// scroll-induced `mouseenter` is not. Tracking is scoped to the DOCUMENT that
// owns the menu's DOM (iframe-safe) and refcounted across concurrent menus.
type PointerTracking = {
  count: number;
  moved: boolean;
  onPointerMove: () => void;
};
const pointerTrackingByDocument = new WeakMap<Document, PointerTracking>();
function markPointerMoved(doc: Document) {
  const tracking = pointerTrackingByDocument.get(doc);
  if (tracking) {
    tracking.moved = true;
  }
}

// Returns true (and resets the flag) if the pointer moved since the last call.
export function consumePointerMoved(doc: Document): boolean {
  const tracking = pointerTrackingByDocument.get(doc);
  if (!tracking) {
    return false;
  }
  const moved = tracking.moved;
  tracking.moved = false;
  return moved;
}

// Refcounted install of the per-document pointer listeners. Returns a cleanup
// that detaches them once the last roving-focus menu in that document unmounts.
export function registerPointerTracking(doc: Document): () => void {
  let tracking = pointerTrackingByDocument.get(doc);
  if (!tracking) {
    const entry: PointerTracking = {
      count: 0,
      moved: false,
      onPointerMove: () => markPointerMoved(doc)
    };
    doc.addEventListener('mousemove', entry.onPointerMove, true);
    doc.addEventListener('pointermove', entry.onPointerMove, true);
    pointerTrackingByDocument.set(doc, entry);
    tracking = entry;
  }
  tracking.count += 1;
  return () => {
    const current = pointerTrackingByDocument.get(doc);
    if (!current) {
      return;
    }
    current.count -= 1;
    if (current.count <= 0) {
      doc.removeEventListener('mousemove', current.onPointerMove, true);
      doc.removeEventListener('pointermove', current.onPointerMove, true);
      pointerTrackingByDocument.delete(doc);
    }
  };
}